import { Dispatch } from 'redux';
import { flow, isEmpty, isNull, keys, pickBy } from 'lodash/fp';
import { NextRouter } from 'next/router';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { RootStateOrAny } from 'react-redux';

import {
  BUILD_YOUR_COVER_INVALID_PRODUCT_TYPES,
  BUILD_YOUR_COVER_RESET_PRODUCT,
  BUILD_YOUR_COVER_SELECT_PRODUCT,
  BUILD_YOUR_COVER_SERVICES_CHANGE,
  BUILD_YOUR_COVER_SET_ADD_ON_TYPE,
  BUILD_YOUR_COVER_SKIP_STEP,
  BUILD_YOUR_COVER_STEP_CHANGE,
} from '../action-types';
import { CHOOSE_YOUR_COVER_TYPE_PARAM } from '../../pages/product/util/use-choose-your-cover-journey-type';
import { JOURNEY_TYPE_URL_SLUG } from '../../pages/build-your-cover/constants';
import { JourneyProductType } from '../../pages/build-your-cover/types';
import { ProductType } from '../../types/product';
import ProductService from '../../models/product';
import UrlService from '../../models/url';
import type { ProductType as ProductTypeType, Service } from '../../types/__codegen__/ahm-api/graphql';

export type BuildYourCoverProductType = `${ProductTypeType}`;

type SelectedProducts = Record<ProductTypeType, ProductType['id'] | null>;

const validateSelectedProducts = (selectedProducts: SelectedProducts) =>
  flow(pickBy(isNull), keys)(selectedProducts) as ProductTypeType[];

const setSelectedServices =
  (productType: BuildYourCoverProductType, selectedServices: Service['serviceId'][]) => (dispatch: Dispatch) =>
    dispatch({ productType, selectedServices, type: BUILD_YOUR_COVER_SERVICES_CHANGE });

const setOrUnsetSelectedProduct =
  (productType: BuildYourCoverProductType, selectedProductId: ProductType['id']) =>
  (dispatch: Dispatch, getState: RootStateOrAny) => {
    const {
      buildYourCover: { selectedProducts },
    } = getState();

    // If the product is already selected, deselect it
    if (selectedProducts[productType] === selectedProductId) {
      dispatch({ productType, selectedProductId, type: BUILD_YOUR_COVER_RESET_PRODUCT });
    } else {
      dispatch({ productType, selectedProductId, type: BUILD_YOUR_COVER_SELECT_PRODUCT });
      const newInvalidProductTypes = validateSelectedProducts(selectedProducts);
      dispatch({ newInvalidProductTypes, type: BUILD_YOUR_COVER_INVALID_PRODUCT_TYPES });
    }
  };

const getProductsToValidate = (selectedProducts: SelectedProducts, journeyProductType: JourneyProductType) => {
  if (journeyProductType === PRODUCT_TYPE.EXTRAS) {
    return { [PRODUCT_TYPE.EXTRAS]: selectedProducts.extras } as SelectedProducts;
  }
  if (journeyProductType === PRODUCT_TYPE.HOSPITAL) {
    return { [PRODUCT_TYPE.HOSPITAL]: selectedProducts.hospital } as SelectedProducts;
  }
  return selectedProducts;
};

const getCartProductId = (selectedProducts: SelectedProducts, journeyProductType: JourneyProductType) => {
  if (journeyProductType === PRODUCT_TYPE.EXTRAS) {
    return selectedProducts.extras;
  }
  if (journeyProductType === PRODUCT_TYPE.HOSPITAL) {
    return selectedProducts.hospital;
  }
  return ProductService.getCombinedId(
    { id: selectedProducts.hospital, type: PRODUCT_TYPE.HOSPITAL } as ProductType,
    { id: selectedProducts.extras, type: PRODUCT_TYPE.EXTRAS } as ProductType,
  );
};

const submitReview =
  (selectedProducts: SelectedProducts, router: NextRouter, journeyProductType: JourneyProductType) =>
  (dispatch: Dispatch) => {
    // Validate that all product types are selected and set/clear invalid product types
    const productsToValidate = getProductsToValidate(selectedProducts, journeyProductType);

    const invalidProductTypes = validateSelectedProducts(productsToValidate);
    dispatch({ invalidProductTypes, type: BUILD_YOUR_COVER_INVALID_PRODUCT_TYPES });

    if (isEmpty(invalidProductTypes)) {
      const queryString = `${CHOOSE_YOUR_COVER_TYPE_PARAM}=${JOURNEY_TYPE_URL_SLUG[journeyProductType ?? PRODUCT_TYPE.PACKAGE]}`;

      const cartProductId = getCartProductId(selectedProducts, journeyProductType) ?? undefined;

      const productUrl = `${UrlService.getProductUrl(cartProductId)}?${queryString}`;
      router.push(productUrl);
    }
  };

const skipStep = (serviceType: BuildYourCoverProductType) => (dispatch: Dispatch) =>
  dispatch({ serviceType, type: BUILD_YOUR_COVER_SKIP_STEP });

const setActiveStep = (activeStep: string) => (dispatch: Dispatch) =>
  dispatch({ activeStep, type: BUILD_YOUR_COVER_STEP_CHANGE });

const setAddOnType = (addOnType: BuildYourCoverProductType | null) => (dispatch: Dispatch) =>
  dispatch({ addOnType, type: BUILD_YOUR_COVER_SET_ADD_ON_TYPE });

export default {
  setActiveStep,
  setAddOnType,
  setOrUnsetSelectedProduct,
  setSelectedServices,
  skipStep,
  submitReview,
};
